import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ShieldCheck, Loader2, Lock, Building2, CreditCard } from "lucide-react";
import { toast } from "sonner";

// GlobalPay's 3DS return strips our own query params and instead appends
// ?response={urlencoded JSON} containing txRef, gateway status and (nested)
// the original redirectUrl we registered — which still carries our
// order/id params. Recover them from the blob.
function parseGatewayResponseBlob(params) {
  const blob = params.get("response");
  if (!blob) return {};
  try {
    const parsed = JSON.parse(blob);
    const out = {
      txRef: parsed.txRef || parsed.transactionRef || null,
      gatewayStatus: (parsed.status || "").toLowerCase(),
    };
    if (parsed.redirectUrl) {
      const u = new URL(parsed.redirectUrl);
      out.orderNumber = u.searchParams.get("order");
      out.orderId = u.searchParams.get("id");
    }
    return out;
  } catch {
    return {};
  }
}

// Fallback of last resort: the order we stashed in localStorage just before
// the same-tab 3DS handoff (see Checkout.handleCard3DSRedirect). Survives
// GlobalPay stripping every query param from the return URL. 2h validity —
// long enough for any 3DS challenge, short enough not to hijack a later visit.
function readPendingCardOrder() {
  try {
    const raw = localStorage.getItem("pg_pending_card_order");
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p?.id || !p?.order_number) return null;
    if (Date.now() - (p.ts || 0) > 2 * 60 * 60 * 1000) return null;
    return p;
  } catch {
    return null;
  }
}

function clearPendingCardOrder() {
  try { localStorage.removeItem("pg_pending_card_order"); } catch { /* noop */ }
}

export default function FenaConfirm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const blobInfo = useMemo(() => parseGatewayResponseBlob(params), [params]);
  const pending = useMemo(() => readPendingCardOrder(), []);
  const [resolved, setResolved] = useState(null); // {order_number, id} via txRef lookup
  const orderId = params.get("id") || blobInfo.orderId || resolved?.id || pending?.id;
  const orderNumber = params.get("order") || blobInfo.orderNumber || resolved?.order_number || pending?.order_number;
  const ref = params.get("ref");
  const via = params.get("via");          // "card" when landed from GlobalPay modal
  const verifying = params.get("verifying") === "1";
  const isCardFlow = via === "card" || !!blobInfo.txRef || !!blobInfo.orderId
    || (!!pending && !ref);   // bare 3DS return with zero params → recovered from storage
  // Bare ?order= entry with no gateway context — recovery-email resume links.
  const isResumeFlow = !ref && !isCardFlow && !params.get("id") && !!params.get("order");
  const [resumeState, setResumeState] = useState("checking"); // checking | ready | redirecting
  const [step, setStep] = useState("authorise");
  const [bank, setBank] = useState();
  const [polling, setPolling] = useState(false);

  // Last-resort recovery: the gateway blob had a txRef but no usable
  // redirectUrl — ask the backend which order the transaction belongs to.
  useEffect(() => {
    if (orderNumber || !blobInfo.txRef) return;
    api.get(`/globalpay/by-txref/${encodeURIComponent(blobInfo.txRef)}`)
      .then((r) => setResolved(r.data))
      .catch(() => navigate("/"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const banks = ["Barclays", "NatWest", "HSBC UK", "Lloyds", "Santander UK", "Monzo", "Starling", "Revolut"];

  // Resume flow: one-shot status check (already paid? → straight to confirmation),
  // then offer a fresh Pay-by-Bank link instead of the passive polling UI.
  useEffect(() => {
    if (!isResumeFlow) return;
    api.get(`/orders/public/${orderNumber}`)
      .then(({ data }) => {
        if (data?.payment_status === "paid" || data?.status === "paid") {
          navigate(`/order/${orderNumber}`);
        } else {
          setResumeState("ready");
        }
      })
      .catch(() => navigate("/"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resumePay = async () => {
    setResumeState("redirecting");
    try {
      const { data } = await api.post("/payments/fena/reinitiate", { order_number: orderNumber });
      if (data?.redirect_url?.startsWith("http")) {
        window.location.assign(data.redirect_url);
      } else if (data?.redirect_url) {
        navigate(data.redirect_url); // mock/dev fallback path
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not restart the payment — please try again.";
      toast.error(msg);
      if (e?.response?.status === 409) navigate(`/order/${orderNumber}`);
      setResumeState("ready");
    }
  };

  const confirm = async () => {
    if (!bank) {
      toast.error("Please select your bank");
      return;
    }
    setStep("processing");
    try {
      await new Promise((r) => setTimeout(r, 1600));
      await api.post("/payments/fena/confirm", { order_id: orderId });
      navigate(`/order/${orderNumber}`);
    } catch {
      toast.error("Payment confirmation failed");
      setStep("authorise");
    }
  };

  // Polling loop. Two callers land here:
  //   • Fena Pay-by-Bank   → ?order=X&ref=Y (no id) — we wait for the
  //     gateway webhook to flip payment_status to "paid"
  //   • GlobalPay card     → ?order=X&id=Y&via=card[&verifying=1] —
  //     handed off by GlobalPayCardModal when the gateway sat on
  //     PENDING for 90s. The order is already in flight at GlobalPay;
  //     we poll the public order endpoint (which itself hits the
  //     gateway via /api/globalpay/status/{id}) until settlement.
  useEffect(() => {
    if (isResumeFlow) return; // resume flow has its own one-shot check above
    if (!orderNumber) {
      // Still waiting on the txRef lookup? Don't bounce home yet.
      if (blobInfo.txRef && !resolved) return;
      navigate("/");
      return;
    }
    // Skip polling only for the synthetic Fena mock flow (id set, NOT card).
    // For the card flow we always poll because the order is still in flight.
    if (orderId && !isCardFlow) return;
    setPolling(true);
    let attempts = 0;
    const t = setInterval(async () => {
      attempts += 1;
      try {
        // For the card flow, hit the GlobalPay-aware status endpoint so
        // each poll actively asks the gateway for the latest state. For
        // Fena, the public order endpoint is enough — the webhook flips
        // the status when the bank confirms.
        const url = isCardFlow && orderId
          ? `/globalpay/status/${orderId}`
          : `/orders/public/${orderNumber}`;
        const { data } = await api.get(url);
        if (data?.payment_status === "paid" || data?.status === "paid") {
          clearInterval(t);
          clearPendingCardOrder();
          navigate(`/order/${orderNumber}`);
        } else if (data?.payment_status === "failed") {
          clearInterval(t);
          clearPendingCardOrder();
          navigate(`/order/${orderNumber}`);
        }
      } catch (err) {
        console.warn("[FenaConfirm] poll error (will retry):", err?.message || err);
      }
      if (attempts > (isCardFlow ? 200 : 60)) { // card: ~10 min, bank: ~3 min
        clearInterval(t);
        setPolling(false);
        clearPendingCardOrder();
        toast.message(
          isCardFlow
            ? "We're still waiting on the bank to settle your card payment — we'll email you the moment it lands."
            : "Still waiting for your bank — we'll email you when payment lands."
        );
        navigate(`/order/${orderNumber}`);
      }
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, orderNumber, isCardFlow]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16" data-testid="fena-confirm-page">
      <div className="pg-card p-8">
        {/* ── Card flow landing ───────────────────────────────────────── */}
        {isCardFlow ? (
          <>
            <div className="flex items-center gap-3 mb-6">
              <CreditCard size={26} className="text-[#A6FF00]" />
              <div>
                <span className="pg-display text-2xl text-[#F7FAFC]" style={{ fontFamily: "'Exo 2'" }}>Confirming Card Payment</span>
                <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4]">3-D Secure complete · Awaiting gateway settlement</div>
              </div>
            </div>
            <div className="pg-micro mb-2">// Payment Reference</div>
            <div className="pg-mono text-[11px] uppercase tracking-[0.22em] text-[#9CA8B8] mb-6">
              <span className="text-[#00C2D6]">{orderNumber}</span>
            </div>
            <div className="text-center py-6" data-testid="card-confirm-polling">
              <Loader2 size={36} className="text-[#00C2D6] mx-auto animate-spin mb-4" />
              <h3 className="text-[#F7FAFC] pg-heading text-xl mb-3" style={{ fontFamily: "'Rajdhani'" }}>
                {verifying ? "Waiting on your bank to settle" : "Confirming your payment"}
              </h3>
              <p className="text-[#9CA8B8] text-sm leading-relaxed max-w-md mx-auto">
                3-D Secure passed and the charge is with the gateway. Most settlements land within seconds, but if
                your bank is slow it can take a couple of minutes. You&apos;ll be redirected to your order page
                automatically — and you&apos;ll get a confirmation email as soon as the payment clears.
              </p>
              <div className="mt-6 grid grid-cols-3 gap-2 text-center max-w-md mx-auto">
                <Trust icon={Lock} label="Bank-grade Encryption" />
                <Trust icon={ShieldCheck} label="3-D Secure Verified" />
                <Trust icon={Building2} label="FCA Regulated" />
              </div>
            </div>
          </>
        ) : (
          <>
        <div className="flex items-center gap-3 mb-6">
          <Lock size={26} className="text-[#A6FF00]" />
          <div>
            <span className="pg-display text-2xl text-[#F7FAFC]" style={{ fontFamily: "'Exo 2'" }}>Secure Bank Transfer</span>
            <div className="pg-mono text-[10px] uppercase tracking-[0.22em] text-[#5EEAD4]">256-bit encrypted · UK Open Banking</div>
          </div>
        </div>
        <div className="pg-micro mb-2">// Authorise within your banking app</div>
        <h1 className="text-[#F7FAFC] text-3xl pg-heading mb-3" style={{ fontFamily: "'Rajdhani'" }}>
          Confirm payment with your bank
        </h1>
        <div className="pg-mono text-[11px] uppercase tracking-[0.22em] text-[#9CA8B8] mb-6">
          Payment Reference: <span className="text-[#00C2D6]">{ref || orderNumber}</span>
        </div>

        {isResumeFlow && (
          <div className="py-4" data-testid="resume-payment-panel">
            {resumeState === "checking" ? (
              <div className="text-center py-8">
                <Loader2 size={36} className="text-[#00C2D6] mx-auto animate-spin mb-4" />
                <p className="text-[#9CA8B8] text-sm">Checking your order…</p>
              </div>
            ) : (
              <>
                <h3 className="text-[#F7FAFC] pg-heading text-xl mb-3" style={{ fontFamily: "'Rajdhani'" }}>
                  Complete your payment
                </h3>
                <p className="text-[#9CA8B8] text-sm leading-relaxed mb-6">
                  Your order <span className="text-[#00C2D6]">{orderNumber}</span> is saved and waiting.
                  Tap below and we&apos;ll take you to our secure Pay-by-Bank page — you approve the
                  payment inside your own banking app in a couple of taps.
                </p>
                <button
                  onClick={resumePay}
                  disabled={resumeState === "redirecting"}
                  className="pg-btn-checkout"
                  data-testid="resume-payment-btn"
                >
                  {resumeState === "redirecting"
                    ? (<><Loader2 size={18} className="animate-spin" /> Opening secure payment…</>)
                    : (<><ShieldCheck size={18} /> Pay securely with your bank →</>)}
                </button>
                <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                  <Trust icon={Lock} label="Bank-grade Encryption" />
                  <Trust icon={Building2} label="FCA Regulated" />
                  <Trust icon={ShieldCheck} label="No Card Details" />
                </div>
              </>
            )}
          </div>
        )}

        {!isResumeFlow && polling && !orderId && (
          <div className="text-center py-8" data-testid="fena-polling">
            <Loader2 size={36} className="text-[#00C2D6] mx-auto animate-spin mb-4" />
            <h3 className="text-[#F7FAFC] pg-heading text-xl mb-2" style={{ fontFamily: "'Rajdhani'" }}>
              Awaiting confirmation from your bank
            </h3>
            <p className="text-[#9CA8B8] text-sm">
              You authorised the payment on Fena. We're listening for your bank's confirmation now —
              this usually takes a few seconds. You'll be redirected automatically.
            </p>
          </div>
        )}

        {!isResumeFlow && !polling && step === "authorise" && (
          <>
            <div className="pg-micro mb-3">Select your UK bank</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
              {banks.map((b) => (
                <button key={b} onClick={() => setBank(b)} className={`p-3 text-sm border rounded-sm transition-colors ${bank === b ? "border-[#A6FF00] bg-[#A6FF00]/10 text-[#A6FF00]" : "border-[#00C2D6]/20 text-[#9CA8B8] hover:border-[#00C2D6]/50"}`} data-testid={`bank-${b.toLowerCase().replace(/\s+/g, "-")}`}>
                  {b}
                </button>
              ))}
            </div>
            <button onClick={confirm} className="pg-btn-checkout" data-testid="fena-authorise">
              <ShieldCheck size={18} /> Authorise & Confirm
            </button>
            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              <Trust icon={Lock} label="Bank-grade Encryption" />
              <Trust icon={Building2} label="FCA Regulated" />
              <Trust icon={ShieldCheck} label="No Card Details" />
            </div>
            <p className="mt-4 text-xs text-[#9CA8B8] text-center leading-relaxed">
              You will be redirected to your bank to authorise the transfer. Peptigen Labs never sees your bank login or card details.
            </p>
          </>
        )}
        {step === "processing" && (
          <div className="text-center py-10">
            <Loader2 size={36} className="text-[#00C2D6] mx-auto mb-4 animate-spin" />
            <div className="text-[#F7FAFC] pg-heading text-lg">Authorising with {bank}...</div>
            <div className="text-[#9CA8B8] text-sm mt-2">Please wait while we confirm your payment</div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
}

function Trust({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center gap-1 p-2 border border-[#00C2D6]/20 rounded-sm">
      <Icon size={14} className="text-[#5EEAD4]" />
      <span className="pg-mono text-[8.5px] uppercase tracking-[0.16em] text-[#9CA8B8]">{label}</span>
    </div>
  );
}
